import { BasePrinterExecutor } from './BaseExecutor';

const isElectronRuntime = () => {
  if (typeof window !== 'undefined' && window.electronAPI?.isElectron) {
    return true;
  }
  if (typeof process !== 'undefined' && process.versions?.electron) {
    return true;
  }
  return false;
};

const getElectronAPI = () =>
  (typeof window !== 'undefined' && window.electronAPI) ? window.electronAPI : null;

export class ElectronPrinterExecutor extends BasePrinterExecutor {
  constructor() {
    super({ type: 'electron-main', environment: 'electron' });
    this.currentPrinter = null;
  }

  getCapabilities() {
    const supported = isElectronRuntime() && !!getElectronAPI()?.printer;
    return {
      canDiscover: supported,
      canPair: supported,
      canPrint: supported,
      transports: supported ? ['usb', 'bluetooth'] : [],
      persistentConnection: supported,
      notes: supported
        ? ['Uses native OS printers (USB/Bluetooth) configured in the system.']
        : ['Electron runtime bridge not available.'],
    };
  }

  // List of known virtual printers to exclude
  static VIRTUAL_PRINTER_PATTERNS = [
    /onenote/i,
    /microsoft.*print.*pdf/i,
    /microsoft.*xps/i,
    /fax/i,
    /send.*to.*onenote/i,
    /pdf/i,
    /xps/i,
    /snagit/i,
    /adobe.*pdf/i,
  ];

  static isVirtualPrinter(printerName) {
    if (!printerName) return false;
    return ElectronPrinterExecutor.VIRTUAL_PRINTER_PATTERNS.some(pattern =>
      pattern.test(printerName)
    );
  }

  mapDevice(device) {
    if (!device) return null;
    return {
      id: device.id || device.name,
      name: device.name || 'Printer',
      transport: 'usb',
      address: device.id || device.name,
      isDefault: Boolean(device.isDefault),
      meta: {
        status: device.status || null,
        options: device.options || {},
      },
    };
  }

  async discoverDevices() {
    const api = getElectronAPI();
    if (!api?.printer) {
      return {
        success: false,
        devices: [],
        message: 'Electron printer bridge not available.',
      };
    }
    const response = await api.printer.discover();
    if (!response?.success) {
      return {
        success: false,
        devices: [],
        message: response?.message || 'Unable to enumerate printers.',
      };
    }
    // Filter out virtual printers like OneNote, PDF printers, etc.
    const physicalPrinters = (response.printers || []).filter(
      (printer) => !ElectronPrinterExecutor.isVirtualPrinter(printer.name)
    );
    return {
      success: true,
      devices: physicalPrinters.map((printer) => this.mapDevice(printer)),
    };
  }

  async connect(deviceDescriptor) {
    const api = getElectronAPI();
    if (!api?.printer) {
      return { success: false, message: 'Electron printer bridge not available.' };
    }
    const printerId = deviceDescriptor?.id || deviceDescriptor?.name;
    if (!printerId) {
      return { success: false, message: 'Printer identifier is required.' };
    }
    // Prevent connecting to virtual printers
    if (ElectronPrinterExecutor.isVirtualPrinter(printerId)) {
      return {
        success: false,
        message: 'Cannot connect to virtual printers (e.g., OneNote, PDF printers). Please select a physical printer.',
      };
    }
    const response = await api.printer.connect(printerId);
    if (!response?.success) {
      return { success: false, message: response?.message || 'Failed to connect to printer.' };
    }
    // Double-check the connected printer is not virtual
    if (ElectronPrinterExecutor.isVirtualPrinter(response.printer?.name)) {
      return {
        success: false,
        message: 'Connected printer is a virtual printer. Please select a physical printer.',
      };
    }
    this.currentPrinter = this.mapDevice(response.printer);
    return { success: true, device: this.currentPrinter };
  }

  async disconnect() {
    const api = getElectronAPI();
    if (!api?.printer) {
      return { success: false, message: 'Electron printer bridge not available.' };
    }
    await api.printer.disconnect();
    this.currentPrinter = null;
    return { success: true };
  }

  async print(data, options = {}) {
    const api = getElectronAPI();
    if (!api?.printer) {
      return { success: false, message: 'Electron printer bridge not available.' };
    }
    const targetPrinter =
      options.printer ||
      this.currentPrinter?.id ||
      this.currentPrinter?.name;
    if (!targetPrinter) {
      return { success: false, message: 'No printer selected.' };
    }
    const payload =
      data instanceof Uint8Array ? Array.from(data) : Array.from(data || []);
    const response = await api.printer.print({
      data: payload,
      printer: targetPrinter,
    });
    if (!response?.success) {
      return { success: false, message: response?.message || 'Print failed.' };
    }
    return { success: true };
  }

  async getStatus() {
    const api = getElectronAPI();
    if (!api?.printer) {
      return {
        connected: false,
        message: 'Electron printer bridge not available.',
      };
    }
    const status = await api.printer.status();
    if (status?.success && status.printer) {
      this.currentPrinter = this.mapDevice(status.printer);
    }
    return {
      connected: Boolean(status?.connected),
      printer: this.currentPrinter,
      message: status?.message,
    };
  }

  async restoreConnection(persistedState = {}) {
    if (!persistedState?.device?.id) {
      return { success: false, connected: false };
    }
    // Prevent auto-reconnecting to virtual printers
    if (ElectronPrinterExecutor.isVirtualPrinter(persistedState.device.name || persistedState.device.id)) {
      return {
        success: false,
        connected: false,
        message: 'Saved printer is a virtual printer. Please select a physical printer.',
      };
    }
    const result = await this.connect(persistedState.device);
    return { ...result, connected: result.success };
  }
}

